import { useState, useEffect } from "react";
import { callApiWithOptions, HttpMethod } from "src/services/apiService";
import { getLogo } from "src/services/logoService";
import styles from "./PaywallModal.module.css";

interface PaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEmailVerified: () => void;
  onEmailSubmitted?: (email: string) => void;
  isEmailVerified?: boolean;
  hasEmail?: boolean;
}

const PaywallModal: React.FC<PaywallModalProps> = ({
  isOpen,
  onClose,
  onEmailVerified,
  onEmailSubmitted,
  isEmailVerified = false,
  hasEmail = false,
}) => {
  const [inputEmail, setInputEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [showVerification, setShowVerification] = useState(false);
  const [logo, setLogo] = useState("");
  const [countdown, setCountdown] = useState(5);

  // Helper function to get custom headers with anon_Id from localStorage
  const getCustomHeaders = (): Record<string, string> => {
    const anonId = localStorage.getItem("anon_Id");
    if (anonId) {
      return {
        "X-Anon-ID": anonId,
      };
    }
    return {};
  };

  useEffect(() => {
    const defaultLogo = "/images/DNCScrubLight.png";
    const fetchedLogo = getLogo("");
    if (fetchedLogo) {
      setLogo(fetchedLogo);
    } else {
      setLogo(defaultLogo);
    }
  }, []);

  // Countdown effect for verified email users
  useEffect(() => {
    if (isEmailVerified && hasEmail && isOpen) {
      setCountdown(5);

      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            // Use setTimeout to ensure the redirect happens after state update
            setTimeout(() => {
              breakOutOfFrame("https://www.dnc.com/schedule-meeting/");
            }, 100);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isEmailVerified, hasEmail, isOpen]);

  // Function to break out of iframe and redirect parent
  const breakOutOfFrame = (url: string) => {
    if (window.top && window.top !== window.self) {
      // Site is in an iframe, redirect the parent window
      window.top.location.href = url;
    } else {
      // Site is not in an iframe, redirect in same window
      window.location.href = url;
    }
  };

  const handleSubmitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputEmail.trim()) {
      setError("Please enter a valid email address");
      return;
    }

    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inputEmail.trim())) {
      setError("Please enter a valid email address (e.g., user@example.com)");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const response = await callApiWithOptions({
        endpoint: "ComplianceGuide/RequestEmailVerification",
        method: HttpMethod.POST,
        body: {
          email: inputEmail.trim(),
        },
        customHeaders: getCustomHeaders(),
      });

      if (response.success) {
        setShowVerification(true);
        // Notify parent component about the email
        if (onEmailSubmitted) {
          onEmailSubmitted(inputEmail.trim());
        }
      } else {
        setError(response.message || "Failed to send verification code");
      }
    } catch (error: any) {
      // Check if the error response contains a specific reason phrase
      const errorMessage =
        error.data?.reasonPhrase ||
        error.message ||
        "An error occurred. Please try again.";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verificationCode.trim()) {
      setError("Please enter the verification code");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const response = await callApiWithOptions({
        endpoint: "ComplianceGuide/VerifyEmailCode",
        method: HttpMethod.POST,
        body: {
          email: inputEmail.trim(),
          verificationCode: verificationCode.trim(),
        },
        customHeaders: getCustomHeaders(),
      });

      if (response.success) {
        onEmailVerified();
        onClose();
      } else {
        setError(response.message || "Invalid verification code");
      }
    } catch (error: any) {
      // Check if the error response contains a specific reason phrase
      const errorMessage =
        error.data?.reasonPhrase ||
        error.message ||
        "An error occurred. Please try again.";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    setIsSubmitting(true);
    setError("");

    try {
      const response = await callApiWithOptions({
        endpoint: "ComplianceGuide/RequestEmailVerification",
        method: HttpMethod.POST,
        body: {
          email: inputEmail.trim(),
        },
        customHeaders: getCustomHeaders(),
      });

      if (response.success) {
        setError("");
        // Show success message briefly
        setTimeout(() => setError(""), 3000);
      } else {
        setError(response.message || "Failed to resend verification code");
      }
    } catch (error: any) {
      // Check if the error response contains a specific reason phrase
      const errorMessage =
        error.data?.reasonPhrase ||
        error.message ||
        "An error occurred. Please try again.";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.modalContent}>
          <div className={styles.content}>
            <div className={styles.iconContainer}>
              <img src={logo} alt="DNC Scrub" className={styles.companyLogo} />
            </div>

            {isEmailVerified && hasEmail ? (
              // Show countdown for verified email users
              <>
                <h2 className={styles.title}>You've Reached the Chat Limit</h2>

                <div className={styles.countdownContainer}>
                  <div className={styles.countdownMessage}>
                    <i className="bi bi-clock"></i>
                    You will be redirected in{" "}
                    <span className={styles.countdownNumber}>
                      {countdown}
                    </span>{" "}
                    seconds
                  </div>

                  <div className={styles.countdownProgress}>
                    <div
                      className={styles.countdownBar}
                      style={{ width: `${((5 - countdown) / 5) * 100}%` }}
                    ></div>
                  </div>

                  <button
                    type="button"
                    onClick={() => breakOutOfFrame("https://dnc.com")}
                    className={styles.redirectNowButton}
                  >
                    <i className="bi bi-arrow-right"></i>
                    Redirect Now
                  </button>
                </div>
              </>
            ) : (
              // Show normal content for non-verified users
              <>
                <h2 className={styles.title}>
                  Continue Your Compliance Conversation
                </h2>

                <p className={styles.subtitle}>
                  Enter and verify your email address for more access to the AI
                  Assistant.
                </p>

                <div className={styles.benefits}>
                  <div className={styles.benefit}>
                    <i className="bi bi-check-circle"></i>
                    <span>Get to ask 5 more questions!</span>
                  </div>
                </div>

                {!showVerification ? (
                  <form onSubmit={handleSubmitEmail} className={styles.form}>
                    <div className={styles.inputGroup}>
                      <label htmlFor="email" className={styles.label}>
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={inputEmail}
                        onChange={(e) => setInputEmail(e.target.value)}
                        placeholder="Enter your email address"
                        className={styles.input}
                        required
                        disabled={isSubmitting}
                      />
                    </div>

                    {error && <div className={styles.error}>{error}</div>}

                    <button
                      type="submit"
                      className={styles.submitButton}
                      disabled={isSubmitting || !inputEmail.trim()}
                    >
                      {isSubmitting ? (
                        <>
                          <i className="bi bi-hourglass-split"></i>
                          Sending...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-envelope"></i>
                          Validate your email
                        </>
                      )}
                    </button>

                    <p className={styles.privacy}>
                      By continuing, you agree to our{" "}
                      <a
                        href="https://www.dnc.com/privacy-policy/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </a>{" "}
                      and{" "}
                      <a
                        href="https://www.dnc.com/terms-and-conditions/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms of Service
                      </a>
                      . We'll send you a verification code to confirm your
                      email.
                    </p>
                  </form>
                ) : (
                  <form onSubmit={handleVerifyCode} className={styles.form}>
                    <div className={styles.successMessage}>
                      <i className="bi bi-check-circle"></i>
                      Verification code sent to {inputEmail}
                      <button
                        type="button"
                        onClick={() => {
                          setShowVerification(false);
                          setVerificationCode("");
                          setError("");
                        }}
                        className={styles.changeEmailLink}
                      >
                        Change email
                      </button>
                    </div>

                    <div className={styles.inputGroup}>
                      <label
                        htmlFor="verificationCode"
                        className={styles.label}
                      >
                        Verification Code
                      </label>
                      <input
                        type="text"
                        id="verificationCode"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        placeholder="Enter 6-digit code"
                        className={styles.input}
                        required
                        disabled={isSubmitting}
                        maxLength={6}
                      />
                    </div>

                    {error && <div className={styles.error}>{error}</div>}

                    <button
                      type="submit"
                      className={styles.submitButton}
                      disabled={isSubmitting || !verificationCode.trim()}
                    >
                      {isSubmitting ? (
                        <>
                          <i className="bi bi-hourglass-split"></i>
                          Verifying...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-lg"></i>
                          Verify & Continue
                        </>
                      )}
                    </button>

                    <div className={styles.resendContainer}>
                      <p className={styles.resendText}>
                        Didn't receive the code?
                      </p>
                      <button
                        type="button"
                        onClick={handleResendCode}
                        disabled={isSubmitting}
                        className={styles.resendButton}
                      >
                        {isSubmitting ? "Sending..." : "Resend Code"}
                      </button>
                    </div>
                  </form>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaywallModal;
